/**
 * Lumiari floating-launcher coexistence protocol — `window.__lumiariDock`.
 *
 * Every Lumiari floating widget (chat-widget, live-widget, ticket-widget…) mounts
 * a `position:fixed` launcher in the same viewport corner, so they overlap. This
 * is a tiny, SELF-INSTALLING, IDEMPOTENT coordinator that both bundles ship an
 * identical copy of: the first to run creates `window.__lumiariDock`, the rest
 * reuse it. Each launcher registers its footprint; the coordinator tiles all
 * launchers sharing a corner into ONE lane (stacked, no overlap), ordered
 * deterministically by `order` regardless of which bundle loads first. It
 * replaces the old one-directional "chat sniffs for live and lifts itself" hack
 * with a symmetric, count-agnostic contract.
 */
export interface LumiariDockHandle {
  /** Mark the launcher hidden (e.g. its sheet is open) so others reflow into its slot. */
  setHidden(hidden: boolean): void;
  /** Re-measure + reflow (e.g. the launcher resized at a breakpoint). */
  remeasure(): void;
  /** Remove this launcher from the lane and clear the inline offset it set. */
  unregister(): void;
}

interface Entry {
  id: string;
  el: HTMLElement;
  corner: string;
  order: number;
  hidden: boolean;
}

interface Dock {
  v: number;
  register(opts: { id: string; el: HTMLElement; corner?: string; order?: number }): LumiariDockHandle;
}

const GAP = 14; // px between stacked launchers
const BASE = 16; // px from the viewport edge to the first (corner-most) launcher

function install(): Dock {
  const w = window as unknown as { __lumiariDock?: Dock; requestAnimationFrame?: (cb: () => void) => number };
  const existing = w.__lumiariDock;
  if (existing && typeof existing.register === 'function' && existing.v >= 1) return existing;

  const entries: Entry[] = [];

  const laneOf = (corner: string) =>
    (corner.indexOf('left') >= 0 ? 'left' : 'right') + ':' + (corner.indexOf('top') >= 0 ? 'top' : 'bottom');

  function reflow(): void {
    const lanes: Record<string, Entry[]> = {};
    for (const e of entries) {
      const k = laneOf(e.corner);
      (lanes[k] = lanes[k] || []).push(e);
    }
    for (const key of Object.keys(lanes)) {
      const edge = key.indexOf('top') >= 0 ? 'top' : 'bottom';
      // Deterministic order regardless of registration/load order: lower `order`
      // sits at the corner, higher stacks away from it.
      const list = lanes[key].slice().sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
      let offset = BASE;
      for (const e of list) {
        if (e.hidden || !e.el.isConnected) continue;
        const h = Math.round(e.el.getBoundingClientRect().height) || 56;
        e.el.style.setProperty(edge, offset + 'px', 'important');
        offset += h + GAP;
      }
    }
  }

  let raf = 0;
  function schedule(): void {
    if (raf) return;
    const run = () => {
      raf = 0;
      reflow();
    };
    raf = w.requestAnimationFrame ? w.requestAnimationFrame(run) : (setTimeout(run, 0) as unknown as number);
  }

  if (typeof window.addEventListener === 'function') {
    window.addEventListener('resize', schedule, { passive: true });
  }

  const dock: Dock = {
    v: 1,
    register(opts) {
      const entry: Entry = {
        id: opts.id,
        el: opts.el,
        corner: opts.corner || 'bottom-right',
        order: typeof opts.order === 'number' ? opts.order : 50,
        hidden: false,
      };
      // De-dupe by id (a remount replaces the old entry).
      for (let i = entries.length - 1; i >= 0; i--) {
        if (entries[i].id === entry.id) entries.splice(i, 1);
      }
      entries.push(entry);
      // Position synchronously (no first-paint flash for launchers whose owner
      // doesn't set an initial `bottom`), then re-measure after the entrance
      // animation + a resize/DOM settle.
      reflow();
      setTimeout(schedule, 500);
      return {
        setHidden(hidden: boolean) {
          entry.hidden = !!hidden;
          schedule();
        },
        remeasure() {
          schedule();
        },
        unregister() {
          const i = entries.indexOf(entry);
          if (i >= 0) entries.splice(i, 1);
          entry.el.style.removeProperty('bottom');
          entry.el.style.removeProperty('top');
          schedule();
        },
      };
    },
  };
  w.__lumiariDock = dock;
  return dock;
}

export const lumiariDock: Dock = install();
